import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AccountPage from './AccountPage';
import DashboardPage from './DashboardPage';
import MyAccountPage, { LimitBar } from './MyAccountPage';
import PlansPage from './PlansPage';
import SpamScorePreview, { buildSpamScorePreview } from './SpamScorePreview';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

/* ── shared tokens ── */
const THEMES = {
  dark: {
    bg: '#0f1117', panel: '#13171f', border: '#1e2535',
    text: '#e2e8f0', muted: '#4a5568', subtle: '#94a3b8',
    accent: '#6366f1', accentHover: '#4f46e5',
    danger: '#ef4444', dangerHover: '#dc2626',
    green: '#34d399',
  },
  light: {
    bg: '#f8fafc', panel: '#ffffff', border: '#e2e8f0',
    text: '#0f172a', muted: '#64748b', subtle: '#334155',
    accent: '#6366f1', accentHover: '#4f46e5',
    danger: '#ef4444', dangerHover: '#dc2626',
    green: '#10b981',
  }
};

const S = (C) => ({
  /* app shell */
  app: { display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: C.bg, color: C.text, fontFamily: "'Inter',sans-serif" },

  /* top nav */
  nav: { display: 'flex', alignItems: 'center', gap: 0, background: C.panel, borderBottom: `1px solid ${C.border}`, padding: '0 20px', flexShrink: 0 },
  navBrand: { display: 'flex', alignItems: 'center', gap: 9, marginRight: 28 },
  navIcon: { width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, boxShadow: '0 0 12px rgba(99,102,241,0.4)' },
  navBrandName: { fontSize: 14, fontWeight: 700, color: C.theme === 'dark' ? '#f0f4ff' : C.text, letterSpacing: '-0.3px' },
  navBrandSub: { fontSize: 9, color: C.muted, fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase' },
  navTab: (a) => ({ padding: '14px 16px', fontSize: 12, fontWeight: a ? 600 : 400, color: a ? '#a78bfa' : C.muted, borderBottom: a ? '2px solid #7c3aed' : '2px solid transparent', background: 'none', border: 'none', cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s', letterSpacing: '0.2px' }),
  navRight: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 },
  liveDot: (ok) => ({ width: 7, height: 7, borderRadius: '50%', background: ok ? C.green : '#f87171', boxShadow: ok ? `0 0 6px ${ok ? C.green : '#f87171'}70` : 'none', animation: ok ? 'pulse 2s infinite' : 'none' }),
  liveLabel: { fontSize: 10, color: C.muted },
  themeBtn: { background: 'none', border: 'none', color: C.muted, fontSize: 16, cursor: 'pointer', padding: 4 },

  /* inbox layout */
  inboxWrap: { display: 'flex', flex: 1, overflow: 'hidden' },

  /* left list panel */
  listPanel: { width: 320, minWidth: 320, background: C.panel, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  listTop: { padding: '14px 14px 10px', borderBottom: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 10 },
  listSubhead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  listTitle: { fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' },
  badge: (n) => ({ background: n > 0 ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : C.border, color: n > 0 ? '#fff' : C.muted, fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px', minWidth: 22, textAlign: 'center' }),
  searchWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: 9, fontSize: 11, color: C.muted, pointerEvents: 'none' },
  searchInput: { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 9px 7px 28px', fontSize: 12, color: C.subtle, outline: 'none', fontFamily: "'Inter',sans-serif" },
  listScroll: { flex: 1, overflowY: 'auto', padding: '4px 0' },
  emailItem: (sel) => ({ padding: '11px 14px', cursor: 'pointer', borderLeft: sel ? `3px solid ${C.accent}` : '3px solid transparent', background: sel ? `${C.accent}14` : 'transparent', borderBottom: `1px solid ${C.border}`, transition: 'all 0.15s' }),
  emailFrom: { fontSize: 12, fontWeight: 600, color: C.subtle, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  emailSubj: { fontSize: 12, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 5 },
  emailDate: { fontSize: 10, color: C.muted, fontWeight: 500 },

  /* right detail panel */
  detailPanel: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  detailHead: { padding: '16px 24px', borderBottom: `1px solid ${C.border}`, background: C.panel },
  detailSubject: { fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 10, letterSpacing: '-0.2px' },
  metaGrid: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px 10px', alignItems: 'start' },
  metaLabel: { fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px' },
  metaValue: { fontSize: 12, color: C.subtle, wordBreak: 'break-all' },
  tabBar: { display: 'flex', padding: '0 24px', borderBottom: `1px solid ${C.border}`, background: C.panel },
  tab: (a) => ({ padding: '10px 16px', fontSize: 12, fontWeight: a ? 600 : 400, color: a ? '#a78bfa' : C.muted, borderBottom: a ? '2px solid #7c3aed' : '2px solid transparent', cursor: 'pointer', background: 'none', border: 'none', marginBottom: -1, transition: 'all 0.15s' }),
  detailBody: { flex: 1, overflowY: 'auto', padding: '22px 24px', display: 'flex', flexDirection: 'column' },
  raw: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, fontSize: 12, lineHeight: 1.7, color: C.muted, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: "'Consolas',monospace", flex: 1 },
  iframe: { width: '100%', border: 'none', borderRadius: 10, background: '#fff', minHeight: 'calc(100vh - 220px)', flex: 1 },
  attList: { display: 'flex', flexDirection: 'column', gap: 10 },
  attItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 },
  attName: { fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 8 },
  attMeta: { fontSize: 11, color: C.muted },
  attBtn: { background: `${C.accent}1A`, color: '#a78bfa', border: `1px solid ${C.accent}33`, padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' },

  /* empty / welcome */
  empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 },
  emptyIcon: { fontSize: 38, marginBottom: 4, color: C.muted },
  emptyText: { fontSize: 13, fontWeight: 500, color: C.muted, textAlign: 'center' },
  emptySub: { fontSize: 11, color: C.muted, textAlign: 'center', lineHeight: 1.6 },
  smtpBadge: { marginTop: 6, background: `${C.accent}1A`, border: `1px solid ${C.accent}33`, borderRadius: 8, padding: '9px 18px', fontSize: 12, color: '#7c6ff2', fontFamily: 'monospace' },

  /* ── Account / users CRUD ── */
  accountWrap: { flex: 1, overflowY: 'auto', padding: '28px 32px' },
  accountTitle: { fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 },
  accountSub: { fontSize: 12, color: C.muted, marginBottom: 20 },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: `linear-gradient(135deg,${C.accent},#8b5cf6)`, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: `0 0 14px ${C.accent}4D`, marginBottom: 20 },

  /* table */
  tableWrap: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '11px 16px', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, background: C.bg },
  td: { padding: '12px 16px', fontSize: 13, color: C.subtle, borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle' },
  tdId: { fontSize: 12, color: C.muted, fontFamily: 'monospace' },
  tdUser: { fontWeight: 600, color: C.text },
  tdPass: { fontFamily: 'monospace', fontSize: 12, color: C.muted, letterSpacing: '2px' },
  editBtn: { padding: '5px 12px', fontSize: 11, fontWeight: 600, color: '#a78bfa', background: `${C.accent}1A`, border: `1px solid ${C.accent}33`, borderRadius: 6, cursor: 'pointer', marginRight: 6 },
  delBtn: { padding: '5px 12px', fontSize: 11, fontWeight: 600, color: C.danger, background: `${C.danger}14`, border: `1px solid ${C.danger}33`, borderRadius: 6, cursor: 'pointer' },

  /* modal */
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '28px 28px 24px', width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  modalTitle: { fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 },
  input: { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, color: C.text, outline: 'none', fontFamily: "'Inter',sans-serif", marginBottom: 16 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
  cancelBtn: { padding: '8px 18px', fontSize: 13, fontWeight: 500, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, cursor: 'pointer' },
  saveBtn: { padding: '8px 20px', fontSize: 13, fontWeight: 600, color: '#fff', background: `linear-gradient(135deg,${C.accent},#8b5cf6)`, border: 'none', borderRadius: 8, cursor: 'pointer' },
  errMsg: { fontSize: 12, color: C.danger, marginBottom: 12, padding: '8px 12px', background: `${C.danger}14`, borderRadius: 6 },

  /* plans */
  plansWrap: { flex: 1, overflowY: 'auto', padding: '28px 32px 40px' },
  plansHero: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.25fr) minmax(280px, 0.75fr)',
    gap: 18,
    marginBottom: 22,
    alignItems: 'stretch'
  },
  plansHeroCard: {
    background: `linear-gradient(135deg, ${C.panel}, ${C.bg})`,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: '24px 24px 22px',
    position: 'relative',
    overflow: 'hidden'
  },
  plansHeroKicker: { fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 10 },
  plansHeroTitle: { fontSize: 28, fontWeight: 800, color: C.text, lineHeight: 1.15, letterSpacing: '-0.7px', maxWidth: 520 },
  plansHeroText: { fontSize: 13, lineHeight: 1.7, color: C.muted, marginTop: 12, maxWidth: 560 },
  plansCurrentCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 18, padding: '22px 22px 18px' },
  plansCurrentLabel: { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 8 },
  plansCurrentName: { fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 6 },
  plansCurrentText: { fontSize: 12, color: C.muted, lineHeight: 1.7 },
  plansGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 },
  planCard: (accent, active) => ({
    background: C.panel,
    border: `1px solid ${active ? accent : C.border}`,
    borderRadius: 18,
    padding: '20px 20px 18px',
    boxShadow: active ? `0 0 0 1px ${accent}33, 0 16px 40px ${accent}18` : 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minHeight: 320
  }),
  planBadgeRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  planName: { fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: '-0.3px' },
  planBadge: (accent, active) => ({
    padding: '5px 10px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: active ? accent : C.muted,
    background: active ? `${accent}1A` : C.bg,
    border: `1px solid ${active ? `${accent}4D` : C.border}`
  }),
  planPriceRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
  planPrice: { fontSize: 36, fontWeight: 800, color: C.text, letterSpacing: '-1px' },
  planBilling: { fontSize: 13, color: C.muted, fontWeight: 600 },
  planSummary: { fontSize: 12, lineHeight: 1.7, color: C.muted, minHeight: 42 },
  planFeatureList: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 2 },
  planFeatureItem: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, color: C.subtle, lineHeight: 1.6 },
  planFeatureDot: (accent) => ({
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: accent,
    background: `${accent}1A`,
    border: `1px solid ${accent}33`,
    marginTop: 1
  }),
});

/* ── helpers ── */
function parseEmail(raw = '') {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const headers = {};
  let i = 0;
  for (; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') { i++; break; }
    if ((line.startsWith(' ') || line.startsWith('\t')) && Object.keys(headers).length > 0) {
      const last = Object.keys(headers).pop();
      headers[last] = (headers[last] || '') + ' ' + line.trim();
      continue;
    }
    const c = line.indexOf(':');
    if (c > 0) { headers[line.substring(0, c).trim().toLowerCase()] = line.substring(c + 1).trim(); }
  }
  return { headers, body: lines.slice(i).join('\n') };
}
const fmt = (s) => { try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s || '—'; } };
function relTime(s) {
  if (!s) return '';
  const d = (Date.now() - new Date(s)) / 1000;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return fmt(s);
}

async function handleDownload(att, idx) {
  try {
    // Sanitize MIME type (remove parameters like '; name="file.zip"')
    const mime = (att.contentType || 'application/octet-stream').split(';')[0].trim();
    // Use native fetch to convert base64 to Blob - much safer for binary like .zip
    const res = await fetch(`data:${mime};base64,${att.content.replace(/\s+/g, '')}`);
    const blob = await res.blob();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.filename || `attachment-${idx}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (err) {
    console.error('Failed to download attachment:', err);
    alert('Failed to download attachment. It may be corrupted.');
  }
}

function LoginPage({ onLogin, C }) {
  const [mode, setMode] = useState('login');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const STYLES = S(C);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      if (mode === 'register') {
        await axios.post(`${API}/api/register`, { full_name: fullName, username, password, plan: 'free' });
      }

      const res = await axios.post(`${API}/api/login`, { username, password });
      onLogin(res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || (mode === 'register' ? 'Registration failed' : 'Login failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setFullName('');
    setConfirmPassword('');
  };

  return (
    <div style={{ ...STYLES.empty, background: C.bg }}>
      <div style={{ ...STYLES.modal, width: 360 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
          <button
            type="button"
            onClick={() => switchMode('login')}
            style={{
              ...STYLES.themeBtn,
              flex: 1,
              padding: '8px 10px',
              borderRadius: 8,
              background: mode === 'login' ? C.panel : 'transparent',
              color: mode === 'login' ? C.accent : C.muted,
              border: mode === 'login' ? `1px solid ${C.border}` : '1px solid transparent',
              fontSize: 12,
              fontWeight: 700
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => switchMode('register')}
            style={{
              ...STYLES.themeBtn,
              flex: 1,
              padding: '8px 10px',
              borderRadius: 8,
              background: mode === 'register' ? C.panel : 'transparent',
              color: mode === 'register' ? C.accent : C.muted,
              border: mode === 'register' ? `1px solid ${C.border}` : '1px solid transparent',
              fontSize: 12,
              fontWeight: 700
            }}
          >
            Register
          </button>
        </div>
        <div style={{ ...STYLES.modalTitle, textAlign: 'center' }}>{mode === 'register' ? 'Create your account' : 'Login to Mailtrap Clone'}</div>
        {mode === 'register' && (
          <div style={{ ...STYLES.accountSub, textAlign: 'center', marginBottom: 16 }}>
            New accounts start on the Free plan.
          </div>
        )}
        {error && <div style={STYLES.errMsg}>{error}</div>}
        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              <label style={STYLES.label}>Full Name</label>
              <input style={STYLES.input} value={fullName} onChange={e => setFullName(e.target.value)} required />
            </>
          )}
          <label style={STYLES.label}>Email</label>
          <input style={STYLES.input} value={username} onChange={e => setUsername(e.target.value)} required />
          <label style={STYLES.label}>Password</label>
          <input style={STYLES.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          {mode === 'register' && (
            <>
              <label style={STYLES.label}>Confirm Password</label>
              <input style={STYLES.input} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
            </>
          )}
          <button style={{ ...STYLES.saveBtn, width: '100%', marginTop: 10, opacity: submitting ? 0.8 : 1 }} type="submit" disabled={submitting}>
            {submitting ? (mode === 'register' ? 'Creating account...' : 'Signing in...') : (mode === 'register' ? 'Create Free Account' : 'Sign In')}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════════════
   Main App
════════════════════════════════ */
export default function App() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [page, setPage] = useState('inbox'); // 'inbox' | 'plans' | 'my-account' | 'account'
  const [emails, setEmails] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountErr, setAccountErr] = useState('');
  const [planData, setPlanData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansErr, setPlansErr] = useState('');
  const [planErr, setPlanErr] = useState('');
  const [smtpLogs, setSmtpLogs] = useState([]);
  const [smtpLogsLoading, setSmtpLogsLoading] = useState(false);
  const [smtpLogsErr, setSmtpLogsErr] = useState('');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('html');
  const [live, setLive] = useState(true);
  const [query, setQuery] = useState('');

  const C = { ...THEMES[theme], theme };
  const STYLES = S(C);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleLogin = (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setEmails([]);
    setSmtpLogs([]);
    setAccountInfo(null);
    setAccountErr('');
    setSelected(null);
  };

  const fetchEmails = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/api/emails`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setEmails(r.data); setLive(true); })
      .catch((e) => {
        setLive(false);
        if (e.response && e.response.status === 401) handleLogout();
      });
  }, [token]);

  const handleDeleteEmail = async (id) => {
    if (!window.confirm('Delete this email?')) return;
    try {
      await axios.delete(`${API}/api/emails/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (selected?.id === id) setSelected(null);
      fetchEmails();
    } catch (e) {
      alert('Failed to delete email');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL emails? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/emails`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(null);
      fetchEmails();
    } catch (e) {
      alert('Failed to delete all emails');
    }
  };

  const fetchPlanData = useCallback(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      const username = payload.username;
      axios.get(`${API}/api/current-plan?username=${username}`)
        .then(res => setPlanData(res.data))
        .catch(e => setPlanErr(e.response?.data?.error || 'Failed to load plan information'));
    } catch (e) {
      setPlanErr('Invalid token');
    }
  }, [token]);

  const fetchAccountInfo = useCallback(() => {
    if (!token) return;
    setAccountLoading(true);
    setAccountErr('');

    axios.get(`${API}/api/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAccountInfo(res.data))
      .catch((e) => {
        if (e.response && e.response.status === 401) {
          handleLogout();
          return;
        }
        setAccountErr(e.response?.data?.error || 'Failed to load account information');
      })
      .finally(() => setAccountLoading(false));
  }, [token]);

  const fetchPlans = useCallback(() => {
    if (!token) return;
    setPlansLoading(true);
    setPlansErr('');

    axios.get(`${API}/api/plans`)
      .then((res) => setPlans(res.data))
      .catch((e) => setPlansErr(e.response?.data?.error || 'Failed to load plans'))
      .finally(() => setPlansLoading(false));
  }, [token]);

  const fetchSmtpLogs = useCallback(() => {
    if (!token) return;
    setSmtpLogsLoading(true);
    setSmtpLogsErr('');

    axios.get(`${API}/api/smtp-logs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setSmtpLogs(res.data))
      .catch((e) => {
        if (e.response && e.response.status === 401) {
          handleLogout();
          return;
        }
        setSmtpLogsErr(e.response?.data?.error || 'Failed to load SMTP logs');
      })
      .finally(() => setSmtpLogsLoading(false));
  }, [token]);

  useEffect(() => {
    fetchEmails();
    fetchAccountInfo();
    fetchPlanData();
    fetchPlans();
    fetchSmtpLogs();
  }, [fetchAccountInfo, fetchEmails, fetchPlanData, fetchPlans, fetchSmtpLogs]);

  useEffect(() => {
    if (!token) return undefined;

    const stream = new EventSource(`${API}/api/emails/stream?token=${encodeURIComponent(token)}`);

    stream.onopen = () => setLive(true);
    stream.onerror = () => setLive(false);
    stream.addEventListener('ready', () => setLive(true));
    stream.addEventListener('email', (event) => {
      try {
        const nextEmail = JSON.parse(event.data);
        setEmails((prev) => {
          if (prev.some((email) => email.id === nextEmail.id)) {
            return prev;
          }
          return [nextEmail, ...prev];
        });
      } catch (error) {
        console.error('Failed to parse streamed email:', error);
      }
    });

    return () => stream.close();
  }, [token]);

  const parsed = selected ? parseEmail(selected.raw || '') : null;
  const subject = parsed?.headers['subject'] || '(no subject)';
  const from = parsed?.headers['from'] || `#${selected?.id}` || '—';
  const to = parsed?.headers['to'] || '—';
  const date = parsed?.headers['date'] || selected?.received_at;
  const body = parsed?.body || '';
  const htmlPart = selected?.html || '';
  const hasHtml = !!htmlPart.trim();
  const spamPreview = selected ? buildSpamScorePreview({ parsed, body, htmlPart, attachments: selected.attachments }) : null;

  const q = query.trim().toLowerCase();
  const filtered = q ? emails.filter(e => {
    const p = parseEmail(e.raw);
    return (p.headers['from'] || '').toLowerCase().includes(q) || (p.headers['subject'] || '').toLowerCase().includes(q);
  }) : emails;

  return (
    <div style={STYLES.app}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
        .email-item:hover { background: ${C.accent}10 !important; }
        .nav-tab:hover { color: #a78bfa !important; }
        .tab-btn:hover { color: #a78bfa !important; }
        .detail-in { animation: fadeIn 0.2s ease; }
        .search-input:focus { border-color: ${C.accent} !important; }
        .search-input::placeholder { color: ${C.muted}; }
        .modal-input:focus { border-color: ${C.accent} !important; }
        .trow:hover td { background: ${C.accent}08; }
      `}</style>

      {/* ── Top Nav ── */}
      <nav style={STYLES.nav}>
        <div style={STYLES.navBrand}>
          <div style={STYLES.navIcon}>✉</div>
          <div>
            <div style={STYLES.navBrandName}>MRD MailCapture</div>
            <div style={STYLES.navBrandSub}>A email testing tool for developers</div>
          </div>
        </div>
        {token && [['inbox', '📥 Inbox'], ['dashboard', '📊 Dashboard'], ['plans', '💳 Plans'], ['my-account', '🛡️ My Account'], ['account', '⚙️ Admin']].map(([key, label]) => (
          <button key={key} className="nav-tab" style={STYLES.navTab(page === key)} onClick={() => setPage(key)}>{label}</button>
        ))}
        <div style={STYLES.navRight}>
          <div style={{ display: 'flex', background: C.bg, borderRadius: 20, padding: 2, border: `1px solid ${C.border}`, marginRight: 10 }}>
            <button
              onClick={() => { setTheme('light'); localStorage.setItem('theme', 'light'); }}
              style={{
                ...STYLES.themeBtn,
                fontSize: 10,
                padding: '4px 10px',
                borderRadius: 18,
                background: theme === 'light' ? C.panel : 'transparent',
                color: theme === 'light' ? C.accent : C.muted,
                fontWeight: theme === 'light' ? 600 : 400,
                transition: 'all 0.2s'
              }}
            >☀️ Light</button>
            <button
              onClick={() => { setTheme('dark'); localStorage.setItem('theme', 'dark'); }}
              style={{
                ...STYLES.themeBtn,
                fontSize: 10,
                padding: '4px 10px',
                borderRadius: 18,
                background: theme === 'dark' ? C.panel : 'transparent',
                color: theme === 'dark' ? C.accent : C.muted,
                fontWeight: theme === 'dark' ? 600 : 400,
                transition: 'all 0.2s'
              }}
            >🌙 Dark</button>
          </div>
          {token && (
            <>
              <button style={{ ...STYLES.cancelBtn, padding: '4px 10px', fontSize: 11 }} onClick={handleLogout}>Logout</button>
              <div style={STYLES.liveDot(live)} />
              <span style={STYLES.liveLabel}>{live ? 'Live' : 'Offline'}</span>
            </>
          )}
        </div>
      </nav>

      {/* ── Pages ── */}
      {!token ? (
        <LoginPage onLogin={handleLogin} C={C} />
      ) : page === 'dashboard' ? (
        <DashboardPage emails={emails} planData={planData} smtpLogs={smtpLogs} smtpLogsLoading={smtpLogsLoading} smtpLogsErr={smtpLogsErr} C={C} STYLES={STYLES} />
      ) : page === 'plans' ? (
        <PlansPage planData={planData} plans={plans} loading={plansLoading} err={plansErr} STYLES={STYLES} />
      ) : page === 'my-account' ? (
        <MyAccountPage
          accountInfo={accountInfo}
          accountLoading={accountLoading}
          accountErr={accountErr}
          planData={planData}
          err={planErr}
          emailsCount={emails.length}
          C={C}
          STYLES={STYLES}
        />
      ) : page === 'account' ? (
        <AccountPage C={C} STYLES={STYLES} API={API} />
      ) : (
        <div style={STYLES.inboxWrap}>
          {/* Left: email list */}
          <section style={STYLES.listPanel}>
            <div style={STYLES.listTop}>
              <div style={STYLES.listSubhead}>
                <span style={STYLES.listTitle}>📥 Inbox</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {emails.length > 0 && <button style={{ ...STYLES.delBtn, padding: '3px 8px', fontSize: 10 }} onClick={handleDeleteAll}>Clear</button>}
                  <span style={STYLES.badge(filtered.length)}>{filtered.length}{q ? `/${emails.length}` : ''}</span>
                </div>
              </div>
              <div style={STYLES.searchWrap}>
                <span style={STYLES.searchIcon}>🔍</span>
                <input id="inbox-search" className="search-input" style={STYLES.searchInput} type="text"
                  placeholder="Search by sender or subject…" value={query} onChange={e => setQuery(e.target.value)} />
              </div>
              {planData && <LimitBar planData={planData} emailsCount={emails.length} C={C} style={{ marginTop: 2, padding: '0 4px' }} />}
            </div>

            <div style={STYLES.listScroll}>
              {emails.length === 0 ? (
                <div style={STYLES.empty}>
                  <div style={STYLES.emptyIcon}>📭</div>
                  <div style={STYLES.emptyText}>No emails yet</div>
                  <div style={STYLES.emptySub}>Send to <strong style={{ color: C.muted }}>localhost:2526</strong> and it appears here.</div>
                </div>
              ) : filtered.length === 0 ? (
                <div style={STYLES.empty}>
                  <div style={STYLES.emptyIcon}>🔍</div>
                  <div style={STYLES.emptyText}>No results</div>
                  <div style={STYLES.emptySub}>No emails match "{query}"</div>
                </div>
              ) : filtered.map(email => {
                const p = parseEmail(email.raw);
                const sel = selected?.id === email.id;
                return (
                  <div key={email.id} className="email-item" style={STYLES.emailItem(sel)}
                    onClick={() => { setSelected(email); setTab('html'); }}>
                    <div style={STYLES.emailFrom}>
                      {p.headers['from'] || `#${email.id}`}
                      {email.attachments && email.attachments.length > 0 && <span style={{ marginLeft: 6, fontSize: 10 }}>📎</span>}
                    </div>
                    <div style={STYLES.emailSubj}>{p.headers['subject'] || '(no subject)'}</div>
                    <div style={STYLES.emailDate}>{relTime(email.received_at)}</div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Right: detail */}
          <main style={STYLES.detailPanel}>
            {selected ? (
              <div className="detail-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={STYLES.detailHead}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={STYLES.detailSubject}>{subject}</div>
                    <button style={STYLES.delBtn} onClick={() => handleDeleteEmail(selected.id)}>Delete</button>
                  </div>
                  <div style={STYLES.metaGrid}>
                    <span style={STYLES.metaLabel}>From</span><span style={STYLES.metaValue}>{from}</span>
                    <span style={STYLES.metaLabel}>To</span><span style={STYLES.metaValue}>{to}</span>
                    <span style={STYLES.metaLabel}>Date</span><span style={STYLES.metaValue}>{fmt(date)}</span>
                  </div>
                </div>
                <div style={STYLES.tabBar}>
                  {[
                    ['html', '🌐 HTML'],
                    ['text', '📄 Text'],
                    ['raw', '⌨ Raw'],
                    ...(selected?.attachments?.length ? [['attachments', `📎 Attachments (${selected.attachments.length})`]] : []),
                    ['spam-score', '🛡 Spam Score Preview']
                  ].map(([k, lbl]) => (
                    <button key={k} className="tab-btn" style={STYLES.tab(tab === k)} onClick={() => setTab(k)}>{lbl}</button>
                  ))}
                </div>
                <div style={STYLES.detailBody}>
                  {tab === 'html' && (hasHtml
                    ? <iframe style={STYLES.iframe} srcDoc={htmlPart} title="HTML Preview" sandbox="allow-same-origin" />
                    : <div style={{ ...STYLES.raw, color: C.muted, fontStyle: 'italic' }}>No HTML detected — switch to Text tab.</div>
                  )}
                  {tab === 'text' && <pre style={{ ...STYLES.raw, color: C.subtle, fontFamily: C.theme === 'dark' ? "'Consolas',monospace" : "'Inter',sans-serif" }}>{body || '(empty body)'}</pre>}
                  {tab === 'raw' && <pre style={STYLES.raw}>{selected.raw || '(no raw data)'}</pre>}
                  {tab === 'attachments' && (
                    <div style={STYLES.attList}>
                      {selected.attachments?.map((att, i) => (
                        <div key={i} style={STYLES.attItem}>
                          <div>
                            <div style={STYLES.attName}>📎 {att.filename || `attachment-${i}`}</div>
                            <div style={STYLES.attMeta}>{att.contentType} • {Math.round(att.size / 1024)} KB</div>
                          </div>
                          <a
                            style={STYLES.attBtn}
                            href="#"
                            onClick={(e) => { e.preventDefault(); handleDownload(att, i); }}
                          >
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  {tab === 'spam-score' && <SpamScorePreview preview={spamPreview} C={C} />}
                </div>
              </div>
            ) : (
              <div style={STYLES.empty}>
                <div style={{ fontSize: 52 }}>📬</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.muted }}>Select an email to read</div>
                <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', maxWidth: 320, lineHeight: 1.7 }}>
                  Emails sent to your fake SMTP server appear in the inbox.
                </div>
                <div style={STYLES.smtpBadge}>SMTP → localhost : 2526</div>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}